import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FieldArray, FieldPath, FieldValues, PathValue, useFormContext,
} from 'react-hook-form'
import { DepartureTransportMeansType } from 'features/declaration/models/schemas/commonConsignmentSchemas'
import useSoftDeleteFieldArray from 'shared/rhf/useSoftDeleteFieldArray'
import useCodelist, { createTypeOfIdentificationsByModeOfTransportFilter } from 'features/declaration/codelist/api/useCodelist'
import useCodelistOptions from 'features/declaration/codelist/components/useCodelistOptions'
import FormCollapsibleColumn from 'components/p5/FormCollapsibleColumn'
import FormField from 'components/p5/FormField'
import Input from 'components/p5/Input'
import FormSelect from 'components/p5/FormSelect'

import R0855Validator from 'features/declaration/form/validations/R0855'
import E1103Validator from 'features/declaration/form/validations/E1103'
import FormFieldArray from 'components/p5/FormFieldArray'
import { ArrayPathByValue } from 'shared/rhf/models/types'
import useDeclarationFieldRequirements
  from 'features/declaration/form/components/useDeclarationFieldRequirements'

const scope = 'departureTransportMeans'
type DepartureTransportMeansItemBasePath = `departureTransportMeans.${number}`

type DepartureTransportMeansArrayPath<TForm extends FieldValues> = ArrayPathByValue<TForm, DepartureTransportMeansType>

type DepartureTransportMeansProps<
  TForm extends FieldValues,
  TName extends DepartureTransportMeansArrayPath<TForm>,
> = {
  name: TName
  createDefault: () => FieldArray<TForm, DepartureTransportMeansArrayPath<TForm>>
  inlandModeOfTransport: string
}

function DepartureTransportMeans<
  TForm extends FieldValues,
  TName extends DepartureTransportMeansArrayPath<TForm> = DepartureTransportMeansArrayPath<TForm>,
>({ name, createDefault, inlandModeOfTransport }: DepartureTransportMeansProps<TForm, TName>) {
  const {
    control,
    getValues,
    setValue,
  } = useFormContext<TForm>()

  const { t } = useTranslation()

  const {
    visibleFields,
    softRemove,
    appendWithSequence,
  } = useSoftDeleteFieldArray<TForm, DepartureTransportMeansType, DepartureTransportMeansArrayPath<TForm>>({
    control,
    name,
  })

  const [, countriesOptions] = useCodelist('COUNTRIES')
  const inlandTransportIdentificationsFilterPredicate = useMemo(
    () => createTypeOfIdentificationsByModeOfTransportFilter(inlandModeOfTransport),
    [inlandModeOfTransport],
  )
  const {
    options: inlandTransportIdentificationsOptions,
    isSuccess: isInlandTransportIdentificationsLoaded,
  } = useCodelistOptions('TRANSPORT_IDENTIFICATIONS', inlandTransportIdentificationsFilterPredicate)

  useEffect(() => {
    if (!isInlandTransportIdentificationsLoaded) {
      return
    }

    const departureTransportMeans = getValues(name as FieldPath<TForm>) as DepartureTransportMeansType[]

    departureTransportMeans.forEach((item, index) => {
      if (!inlandModeOfTransport || !inlandTransportIdentificationsOptions
        .find((option) => option.value === item.typeOfIdentification)) {
        const typeOfIdentificationPath = `departureTransportMeans.${index}.typeOfIdentification` as FieldPath<TForm>
        setValue(typeOfIdentificationPath, '' as PathValue<TForm, typeof typeOfIdentificationPath>, {
          shouldDirty: true,
          shouldTouch: true,
        })
      }
    })
  }, [getValues, inlandModeOfTransport, inlandTransportIdentificationsOptions, isInlandTransportIdentificationsLoaded, name, setValue])

  const departureTransportMeansMultiplicity = useMemo(() => R0855Validator
    .getDepartureTransportMeansMultiplicity(inlandModeOfTransport), [inlandModeOfTransport])

  const paths = useMemo(() => [
    scope,
    ...visibleFields.map((f) => `${scope}.${f.index}.typeOfIdentification`),
    ...visibleFields.map((f) => `${scope}.${f.index}.identificationNumber`),
    ...visibleFields.map((f) => `${scope}.${f.index}.nationality`),
  ], [visibleFields])

  const fieldRequirements = useDeclarationFieldRequirements('houseConsignment', paths)

  const isAddForbidden = fieldRequirements[scope]?.requirement === 'forbidden'
  const isAddButtonDisabled = isAddForbidden || visibleFields.length >= departureTransportMeansMultiplicity

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-deprture-transport"
        headingLabel={t('declaration.p5.departureTransportMeans')}
        fieldRule={fieldRequirements[scope]}
      >
        {visibleFields.map((fieldWithIndex) => {
          const departureTransportMeans = fieldWithIndex.field
          const innerItemScope = `${scope}.${fieldWithIndex.index}` as DepartureTransportMeansItemBasePath

          return (
            <FormFieldArray
              key={`departureTransportMeans_${departureTransportMeans.id}`}
              titleKey="declaration.p5.sequenceNumber"
              removeItem={() => softRemove(fieldWithIndex.index)}
              sequenceNumber={departureTransportMeans.sequenceNumber + 1}
              hidden={departureTransportMeans.deleted}
            >
              <FormField
                labelKey="declaration.p5.typeOfIdentification"
                fieldRule={fieldRequirements[`${innerItemScope}.typeOfIdentification`]}
              >
                <FormSelect
                  field={`${innerItemScope}.typeOfIdentification`}
                  labelKey="declaration.p5.typeOfIdentification"
                  options={inlandTransportIdentificationsOptions}
                  isDisabled={!inlandModeOfTransport}
                  type="sync"
                  fieldRule={fieldRequirements[`${innerItemScope}.typeOfIdentification`]}
                />
              </FormField>

              <FormField
                labelKey="declaration.p5.identificationNumber"
                fieldRule={fieldRequirements[`${innerItemScope}.identificationNumber`]}
              >
                <Input
                  field={`${innerItemScope}.identificationNumber`}
                  labelKey="declaration.p5.identificationNumber"
                  type="text"
                  maxLength={E1103Validator.getIdentificationNumberMaxLength()}
                  placeholder={t('declaration.p5.identificationNumber')}
                  fieldRule={fieldRequirements[`${innerItemScope}.identificationNumber`]}
                />
              </FormField>

              <FormField
                labelKey="declaration.p5.nationality"
                fieldRule={fieldRequirements[`${innerItemScope}.nationality`]}
              >
                <FormSelect
                  field={`${innerItemScope}.nationality`}
                  labelKey="declaration.p5.nationality"
                  options={countriesOptions}
                  type="sync"
                  fieldRule={fieldRequirements[`${innerItemScope}.nationality`]}
                />
              </FormField>
            </FormFieldArray>
          )
        })}
        <div className="row">
          <div className={`col-12 text-end ${isAddButtonDisabled ? 'cursor--not-allowed' : ''}`}>
            <button
              type="button"
              className="btn btn-sm btn-primary mt-2"
              onClick={() => appendWithSequence(createDefault())}
              disabled={isAddButtonDisabled}
            >
              <i className="fa fa-plus me-2" />
              {t('declaration.p5.addNewTransportMeans')}
            </button>
          </div>
        </div>
      </FormCollapsibleColumn>
    </section>
  )
}

export default DepartureTransportMeans
